/**
 * @file SlidePanelChrome atom — compound chrome for gesture-driven slide-up panels.
 * @description Provides the visual chrome (drag handle, header row, content area) for
 * bottom-anchored sliding panels. Internalizes themed StyleSheet so call sites carry
 * no UNSAFE_style. Gesture detection and animated height management remain the caller's
 * responsibility (see `react-native-gesture-handler` and `react-native-reanimated`).
 * @module components/atoms/SlidePanelChrome
 */
import React from "react";
import { StyleSheet, View, type ViewStyle } from "react-native";
import { RADIUS } from "@/config/radius";

/** Light-mode background (panel surface) */
const LIGHT_BG = "#FFFFFF";
/** Dark-mode background (panel surface) */
const DARK_BG = "#1C1C1E";
/** Light-mode drag-handle pill color */
const HANDLE_LIGHT = "#D1D5DB";
/** Dark-mode drag-handle pill color */
const HANDLE_DARK = "#535252";
/** Light-mode header border color */
const BORDER_LIGHT = "#E5E5E5";

// ---------------------------------------------------------------------------
// SlidePanelChromeHandleArea
// ---------------------------------------------------------------------------

/** Props for {@link SlidePanelChromeHandleArea} */
export interface SlidePanelChromeHandleAreaProps {
  /** Apply dark-mode palette when true. */
  readonly isDark: boolean;
  /** Test / accessibility identifier forwarded to the outer container. */
  readonly testID?: string;
}

/**
 * Renders the drag-indicator area at the top of a slide-up panel.
 * Contains a centred pill that signals the surface is swipeable.
 * @param props - Component props
 * @param props.isDark - Whether the dark-mode palette is active
 * @param props.testID - Accessibility test identifier
 */
const SlidePanelChromeHandleArea = ({
  isDark,
  testID,
}: SlidePanelChromeHandleAreaProps) => (
  <View
    testID={testID}
    style={[
      styles.handleArea,
      { backgroundColor: isDark ? DARK_BG : LIGHT_BG },
    ]}
  >
    <View
      style={[
        styles.handle,
        { backgroundColor: isDark ? HANDLE_DARK : HANDLE_LIGHT },
      ]}
    />
  </View>
);

SlidePanelChromeHandleArea.displayName = "SlidePanelChromeHandleArea";

// ---------------------------------------------------------------------------
// SlidePanelChromeHeader
// ---------------------------------------------------------------------------

/** Props for {@link SlidePanelChromeHeader} */
export interface SlidePanelChromeHeaderProps {
  /** Apply dark-mode palette when true. */
  readonly isDark: boolean;
  /** Header row content — title, back button, action icon, etc. */
  readonly children: React.ReactNode;
  /** Test / accessibility identifier forwarded to the outer container. */
  readonly testID?: string;
}

/**
 * Themed header row for a slide-up panel.
 * Lays out children horizontally, separated from the content area by a
 * hairline border whose colour follows the dark/light palette.
 * @param props - Component props
 * @param props.isDark - Whether the dark-mode palette is active
 * @param props.children - Header row content (title, actions, etc.)
 * @param props.testID - Accessibility test identifier
 */
const SlidePanelChromeHeader = ({
  isDark,
  children,
  testID,
}: SlidePanelChromeHeaderProps) => (
  <View
    testID={testID}
    style={[
      styles.header,
      {
        backgroundColor: isDark ? DARK_BG : LIGHT_BG,
        borderBottomColor: isDark ? HANDLE_DARK : BORDER_LIGHT,
      },
    ]}
  >
    {children}
  </View>
);

SlidePanelChromeHeader.displayName = "SlidePanelChromeHeader";

// ---------------------------------------------------------------------------
// SlidePanelChromeContent
// ---------------------------------------------------------------------------

/** Props for {@link SlidePanelChromeContent} */
export interface SlidePanelChromeContentProps {
  /** Apply dark-mode palette when true. */
  readonly isDark: boolean;
  /** Scrollable or static panel body. */
  readonly children: React.ReactNode;
  /** Test / accessibility identifier forwarded to the outer container. */
  readonly testID?: string;
  /** Additional style overrides (e.g. runtime-measured height). */
  readonly style?: ViewStyle;
}

/**
 * Flex-1 content area for a slide-up panel.
 * Carries the matching themed background so content scrolls against the
 * correct surface without an additional wrapper.
 * @param props - Component props
 * @param props.isDark - Whether the dark-mode palette is active
 * @param props.children - Panel body content
 * @param props.testID - Accessibility test identifier
 * @param props.style - Additional style overrides
 */
const SlidePanelChromeContent = ({
  isDark,
  children,
  testID,
  style,
}: SlidePanelChromeContentProps) => (
  <View
    testID={testID}
    style={[
      styles.content,
      { backgroundColor: isDark ? DARK_BG : LIGHT_BG },
      style,
    ]}
  >
    {children}
  </View>
);

SlidePanelChromeContent.displayName = "SlidePanelChromeContent";

// ---------------------------------------------------------------------------
// Shared StyleSheet
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  handleArea: {
    alignItems: "center",
    paddingTop: 12,
    paddingBottom: 8,
  } as ViewStyle,
  handle: {
    width: 40,
    height: 4,
    borderRadius: RADIUS.full,
  } as ViewStyle,
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } as ViewStyle,
  content: {
    flex: 1,
  } as ViewStyle,
});

export {
  SlidePanelChromeHandleArea,
  SlidePanelChromeHeader,
  SlidePanelChromeContent,
};
